import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, Cpu, Loader2, Database, Brain, Plus, AlertTriangle, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sourceLabel = {
  database: { text: 'Base de datos', color: 'bg-green-500/20 text-green-400' },
  approximate: { text: 'Aproximado', color: 'bg-amber-500/20 text-amber-400' },
  custom: { text: 'Personalizado', color: 'bg-blue-500/20 text-blue-400' },
  ai: { text: 'IA', color: 'bg-purple-500/20 text-purple-400' },
  not_found: { text: 'No encontrado', color: 'bg-red-500/20 text-red-400' },
};

const BoschLookupPage = () => {
  const { token, user } = useAuth();
  const [partNumber, setPartNumber] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const handleLookup = async (e) => {
    e?.preventDefault();
    const clean = partNumber.replace(/[\s\-\.]/g, '');
    if (!clean || clean.length < 8) return toast.error('Introduce un número de referencia válido (mín. 8 dígitos)');
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.get(`${API}/bosch-lookup/${encodeURIComponent(clean)}`, { headers });
      setResult(res.data);
      setHistory(prev => {
        const filtered = prev.filter(h => h.part_number !== res.data.part_number);
        return [res.data, ...filtered].slice(0, 20);
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en la búsqueda');
    } finally { setLoading(false); }
  };

  const handleAiLookup = async () => {
    if (!result?.part_number) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bosch-lookup/${encodeURIComponent(result.part_number)}?force_ai=1`, { headers });
      setResult(res.data);
      setHistory(prev => {
        const filtered = prev.filter(h => h.part_number !== res.data.part_number);
        return [res.data, ...filtered].slice(0, 20);
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al analizar con IA');
    } finally { setLoading(false); }
  };

  const handleAddCustom = async () => {
    const ecu = prompt('Introduce el tipo de ECU para este part number:');
    if (!ecu) return;
    try {
      await axios.post(`${API}/bosch-lookup/custom`, {
        part_number: result.part_number, ecu, fuel: '', oem: [],
      }, { headers });
      toast.success('Guardado correctamente');
      handleLookup(); // Re-lookup to get the custom result
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Cpu className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-white" data-testid="bosch-lookup-title">BOSCH PART TRANSLATOR</h1>
            <p className="text-zinc-500 text-sm">Identifica la ECU por número de referencia Bosch</p>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleLookup} className="mb-6" data-testid="lookup-form">
          <div className="flex gap-2">
            <Input
              value={partNumber}
              onChange={e => setPartNumber(e.target.value)}
              placeholder="Ej: 0281032462"
              className="bg-surface border-white/10 text-white font-mono text-lg h-12 flex-1"
              data-testid="part-number-input"
            />
            <Button type="submit" disabled={loading} className="bg-primary hover:bg-primary/90 h-12 px-6" data-testid="lookup-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
            </Button>
          </div>
          <p className="text-zinc-600 text-xs mt-2">La referencia Bosch empieza por 028 (diesel) o 026 (gasolina)</p>
        </form>

        {/* Result */}
        {result && (
          <div className="bg-surface border border-white/10 rounded-md p-6 mb-6" data-testid="lookup-result">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Referencia Bosch</p>
                <p className="text-white font-mono text-xl font-bold" data-testid="result-part-number">{result.part_number}</p>
              </div>
              <span className={`text-xs font-medium px-2 py-1 rounded ${(sourceLabel[result.source] || sourceLabel.not_found).color}`}>
                {result.source === 'ai' && <Brain className="w-3 h-3 inline mr-1" />}
                {result.source === 'database' && <Database className="w-3 h-3 inline mr-1" />}
                {(sourceLabel[result.source] || sourceLabel.not_found).text}
              </span>
            </div>

            {result.ecu ? (
              <div className="bg-surface-highlight rounded-md p-5 border border-white/5">
                <div className="flex items-center gap-3 mb-3">
                  <Cpu className="w-8 h-8 text-primary" />
                  <div>
                    <p className="text-primary font-heading text-2xl font-bold" data-testid="result-ecu">{result.ecu}</p>
                    <div className="flex items-center gap-3 mt-1">
                      {result.fuel && <span className="text-zinc-400 text-sm">{result.fuel}</span>}
                      {result.oem?.length > 0 && <span className="text-zinc-500 text-sm">{result.oem.join(', ')}</span>}
                    </div>
                  </div>
                </div>
                {result.confidence < 1 && (
                  <div className="flex items-center gap-1 mt-2">
                    <div className="w-full bg-surface rounded-full h-1.5">
                      <div className="bg-primary h-1.5 rounded-full" style={{ width: `${result.confidence * 100}%` }}></div>
                    </div>
                    <span className="text-zinc-500 text-xs ml-2">{(result.confidence * 100).toFixed(0)}%</span>
                  </div>
                )}
                {result.note && (
                  <p className="text-amber-500/70 text-xs mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {result.note}
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-surface-highlight rounded-md p-5 border border-white/5 text-center">
                <AlertTriangle className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                <p className="text-zinc-400 text-sm">No se encontró este número de referencia</p>
                {result.family_hint && (
                  <p className="text-zinc-500 text-xs mt-1">Familia probable: <span className="text-zinc-300">{result.family_hint}</span></p>
                )}
                <div className="flex items-center justify-center gap-2 mt-3">
                  <Button onClick={handleAiLookup} disabled={loading} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="ai-lookup-btn">
                    {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Brain className="w-3 h-3 mr-1" />}
                    Analizar con IA
                  </Button>
                  {user?.is_admin && (
                    <Button onClick={handleAddCustom} variant="outline" size="sm" className="border-white/10 text-zinc-400 hover:text-white" data-testid="add-custom-btn">
                      <Plus className="w-3 h-3 mr-1" /> Añadir manual
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div data-testid="lookup-history">
            <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2">Búsquedas recientes</p>
            <div className="space-y-1">
              {history.map((h, i) => (
                <button key={`${h.part_number}-${i}`} onClick={() => { setPartNumber(h.part_number); setResult(h); }}
                  className="w-full flex items-center justify-between p-2 rounded bg-surface hover:bg-surface-highlight border border-transparent hover:border-white/5 transition-colors text-left"
                  data-testid={`history-item-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-zinc-400 font-mono text-sm">{h.part_number}</span>
                    <ChevronRight className="w-3 h-3 text-zinc-600" />
                    <span className={`font-heading text-sm font-bold ${h.ecu ? 'text-primary' : 'text-zinc-600'}`}>
                      {h.ecu || '???'}
                    </span>
                  </div>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${(sourceLabel[h.source] || sourceLabel.not_found).color}`}>
                    {(sourceLabel[h.source] || sourceLabel.not_found).text}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BoschLookupPage;
